import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const financialsModule: ChecklistModuleDefinition = {
  code: 'financials',
  name: 'Financial Analysis',
  description: 'Purchase analysis, costs, income, and investment returns',
  categories: [
    {
      code: 'purchase_analysis',
      name: 'Purchase Analysis',
      items: [
        {
          code: 'asking_price_evaluation',
          title: 'Asking price evaluation',
          description: 'Assess if asking price is fair',
          isGroupTask: true,
          dataFields: [
            { key: 'asking_price', label: 'Asking Price', type: 'number', unit: 'dollars' },
            { key: 'price_per_acre', label: 'Price Per Acre', type: 'number', unit: 'dollars' },
            { key: 'fair_market_value', label: 'Est. Fair Market Value', type: 'number', unit: 'dollars' },
            { key: 'valuation_method', label: 'Valuation Method', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Significantly below market value' },
            { flag: 'green', condition: 'At or slightly below market' },
            { flag: 'yellow', condition: 'At top of market' },
            { flag: 'red', condition: 'Above market value' },
          ],
        },
        {
          code: 'comp_analysis',
          title: 'Comparable sales analysis',
          description: 'Research recent comparable sales',
          isGroupTask: false,
          dataFields: [
            { key: 'comps_found', label: 'Comps Found', type: 'number' },
            { key: 'avg_price_per_acre', label: 'Avg Price/Acre', type: 'number', unit: 'dollars' },
            { key: 'price_range_low', label: 'Price Range Low', type: 'number', unit: 'dollars' },
            { key: 'price_range_high', label: 'Price Range High', type: 'number', unit: 'dollars' },
            { key: 'notes', label: 'Notes', type: 'string' },
          ],
        },
        {
          code: 'offer_strategy',
          title: 'Offer strategy',
          description: 'Determine offer approach',
          isGroupTask: true,
          dataFields: [
            { key: 'target_purchase_price', label: 'Target Purchase Price', type: 'number', unit: 'dollars' },
            { key: 'initial_offer', label: 'Initial Offer', type: 'number', unit: 'dollars' },
            { key: 'max_price', label: 'Maximum Price', type: 'number', unit: 'dollars' },
            { key: 'negotiation_notes', label: 'Negotiation Notes', type: 'string' },
          ],
        },
        {
          code: 'days_on_market',
          title: 'Days on market analysis',
          description: 'How long property has been listed',
          isGroupTask: false,
          dataFields: [
            { key: 'days_on_market', label: 'Days on Market', type: 'number' },
            { key: 'price_reductions', label: 'Price Reductions', type: 'number' },
            { key: 'original_price', label: 'Original List Price', type: 'number', unit: 'dollars' },
            { key: 'motivated_seller', label: 'Motivated Seller', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Long DOM, motivated seller' },
          ],
        },
      ],
    },
    {
      code: 'acquisition_costs',
      name: 'Acquisition Costs',
      items: [
        {
          code: 'closing_costs',
          title: 'Closing costs estimate',
          description: 'Total estimated closing costs',
          isGroupTask: false,
          dataFields: [
            { key: 'title_insurance', label: 'Title Insurance', type: 'number', unit: 'dollars' },
            { key: 'recording_fees', label: 'Recording Fees', type: 'number', unit: 'dollars' },
            { key: 'attorney_fees', label: 'Attorney Fees', type: 'number', unit: 'dollars' },
            { key: 'survey_cost', label: 'Survey Cost', type: 'number', unit: 'dollars' },
            { key: 'inspection_costs', label: 'Inspection Costs', type: 'number', unit: 'dollars' },
            { key: 'other_costs', label: 'Other Costs', type: 'number', unit: 'dollars' },
            { key: 'total_closing', label: 'Total Closing Costs', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'financing_terms',
          title: 'Financing terms',
          description: 'Loan/financing details',
          isGroupTask: true,
          dataFields: [
            { key: 'financing_type', label: 'Financing Type', type: 'select', options: [
              { value: 'cash', label: 'Cash' },
              { value: 'conventional', label: 'Conventional Loan' },
              { value: 'owner_finance', label: 'Owner Financing' },
              { value: 'usda', label: 'USDA Loan' },
              { value: 'fha', label: 'FHA Loan' },
              { value: 'va', label: 'VA Loan' },
              { value: 'portfolio', label: 'Portfolio/Land Loan' },
            ]},
            { key: 'down_payment_percent', label: 'Down Payment %', type: 'number', unit: 'percent' },
            { key: 'down_payment_amount', label: 'Down Payment Amount', type: 'number', unit: 'dollars' },
            { key: 'loan_amount', label: 'Loan Amount', type: 'number', unit: 'dollars' },
            { key: 'interest_rate', label: 'Interest Rate', type: 'number', unit: 'percent' },
            { key: 'loan_term', label: 'Loan Term', type: 'number', unit: 'years' },
            { key: 'monthly_payment', label: 'Monthly Payment', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'total_cash_needed',
          title: 'Total cash needed at closing',
          description: 'Total out of pocket to close',
          isGroupTask: true,
          dataFields: [
            { key: 'down_payment', label: 'Down Payment', type: 'number', unit: 'dollars' },
            { key: 'closing_costs', label: 'Closing Costs', type: 'number', unit: 'dollars' },
            { key: 'reserves', label: 'Reserves', type: 'number', unit: 'dollars' },
            { key: 'total_cash', label: 'Total Cash Needed', type: 'number', unit: 'dollars' },
          ],
        },
      ],
    },
    {
      code: 'ongoing_costs',
      name: 'Ongoing Costs',
      items: [
        {
          code: 'annual_expenses',
          title: 'Annual expense budget',
          description: 'Estimate annual carrying costs',
          isGroupTask: true,
          dataFields: [
            { key: 'property_taxes', label: 'Property Taxes', type: 'number', unit: 'dollars' },
            { key: 'insurance', label: 'Insurance', type: 'number', unit: 'dollars' },
            { key: 'utilities', label: 'Utilities', type: 'number', unit: 'dollars' },
            { key: 'maintenance', label: 'Maintenance Reserve', type: 'number', unit: 'dollars' },
            { key: 'hoa_fees', label: 'HOA/Association Fees', type: 'number', unit: 'dollars' },
            { key: 'other_annual', label: 'Other Annual', type: 'number', unit: 'dollars' },
            { key: 'total_annual', label: 'Total Annual Expenses', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'insurance_quotes',
          title: 'Insurance quotes',
          description: 'Get insurance quotes',
          isGroupTask: false,
          dataFields: [
            { key: 'homeowners_quote', label: 'Homeowners Insurance', type: 'number', unit: 'dollars' },
            { key: 'liability_quote', label: 'Liability Insurance', type: 'number', unit: 'dollars' },
            { key: 'flood_insurance', label: 'Flood Insurance', type: 'number', unit: 'dollars' },
            { key: 'other_insurance', label: 'Other Insurance', type: 'number', unit: 'dollars' },
            { key: 'total_insurance', label: 'Total Insurance', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Insurance difficult to obtain' },
            { flag: 'yellow', condition: 'High insurance costs' },
          ],
        },
        {
          code: 'mortgage_payment',
          title: 'Monthly mortgage calculation',
          description: 'Principal, interest, taxes, insurance',
          isGroupTask: false,
          dataFields: [
            { key: 'principal_interest', label: 'P&I', type: 'number', unit: 'dollars' },
            { key: 'property_tax_monthly', label: 'Property Tax', type: 'number', unit: 'dollars' },
            { key: 'insurance_monthly', label: 'Insurance', type: 'number', unit: 'dollars' },
            { key: 'pmi', label: 'PMI', type: 'number', unit: 'dollars' },
            { key: 'total_piti', label: 'Total PITI', type: 'number', unit: 'dollars' },
          ],
        },
      ],
    },
    {
      code: 'income_potential',
      name: 'Income Potential',
      items: [
        {
          code: 'rental_income',
          title: 'Rental income potential',
          description: 'Estimate potential rental income',
          isGroupTask: false,
          dataFields: [
            { key: 'market_rent_monthly', label: 'Market Rent (Monthly)', type: 'number', unit: 'dollars' },
            { key: 'market_rent_annual', label: 'Market Rent (Annual)', type: 'number', unit: 'dollars' },
            { key: 'vacancy_rate', label: 'Est. Vacancy Rate', type: 'number', unit: 'percent' },
            { key: 'effective_gross_income', label: 'Effective Gross Income', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'agricultural_income',
          title: 'Agricultural income potential',
          description: 'Farm/ranch income potential',
          isGroupTask: false,
          dataFields: [
            { key: 'crop_income', label: 'Crop Income', type: 'number', unit: 'dollars' },
            { key: 'livestock_income', label: 'Livestock Income', type: 'number', unit: 'dollars' },
            { key: 'lease_income', label: 'Lease Income', type: 'number', unit: 'dollars' },
            { key: 'other_ag_income', label: 'Other Ag Income', type: 'number', unit: 'dollars' },
            { key: 'total_ag_income', label: 'Total Ag Income', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'timber_income',
          title: 'Timber income potential',
          description: 'Timber harvest income',
          isGroupTask: false,
          dataFields: [
            { key: 'standing_timber_value', label: 'Standing Timber Value', type: 'number', unit: 'dollars' },
            { key: 'harvest_schedule', label: 'Harvest Schedule', type: 'string' },
            { key: 'annual_growth_value', label: 'Annual Growth Value', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'hunting_lease_income',
          title: 'Hunting lease income',
          description: 'Potential hunting lease revenue',
          isGroupTask: false,
          dataFields: [
            { key: 'lease_rate_per_acre', label: 'Lease Rate/Acre', type: 'number', unit: 'dollars' },
            { key: 'leasable_acres', label: 'Leasable Acres', type: 'number', unit: 'acres' },
            { key: 'annual_lease_income', label: 'Annual Lease Income', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'other_income',
          title: 'Other income streams',
          description: 'Additional income opportunities',
          isGroupTask: false,
          dataFields: [
            { key: 'income_streams', label: 'Income Streams', type: 'array' },
            { key: 'total_other_income', label: 'Total Other Income', type: 'number', unit: 'dollars' },
          ],
        },
      ],
    },
    {
      code: 'investment_returns',
      name: 'Investment Returns',
      items: [
        {
          code: 'cash_flow_analysis',
          title: 'Cash flow analysis',
          description: 'Net operating income calculation',
          isGroupTask: true,
          dataFields: [
            { key: 'gross_income', label: 'Gross Income', type: 'number', unit: 'dollars' },
            { key: 'operating_expenses', label: 'Operating Expenses', type: 'number', unit: 'dollars' },
            { key: 'net_operating_income', label: 'NOI', type: 'number', unit: 'dollars' },
            { key: 'debt_service', label: 'Debt Service', type: 'number', unit: 'dollars' },
            { key: 'cash_flow', label: 'Cash Flow', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Strong positive cash flow' },
            { flag: 'green', condition: 'Positive cash flow' },
            { flag: 'yellow', condition: 'Break-even' },
            { flag: 'red', condition: 'Negative cash flow' },
          ],
        },
        {
          code: 'cap_rate',
          title: 'Cap rate calculation',
          description: 'Capitalization rate',
          isGroupTask: true,
          dataFields: [
            { key: 'noi', label: 'NOI', type: 'number', unit: 'dollars' },
            { key: 'purchase_price', label: 'Purchase Price', type: 'number', unit: 'dollars' },
            { key: 'cap_rate', label: 'Cap Rate', type: 'number', unit: 'percent' },
            { key: 'market_cap_rate', label: 'Market Cap Rate', type: 'number', unit: 'percent' },
          ],
        },
        {
          code: 'cash_on_cash',
          title: 'Cash-on-cash return',
          description: 'Return on invested cash',
          isGroupTask: true,
          dataFields: [
            { key: 'annual_cash_flow', label: 'Annual Cash Flow', type: 'number', unit: 'dollars' },
            { key: 'total_cash_invested', label: 'Total Cash Invested', type: 'number', unit: 'dollars' },
            { key: 'cash_on_cash_return', label: 'Cash-on-Cash Return', type: 'number', unit: 'percent' },
          ],
        },
        {
          code: 'appreciation_potential',
          title: 'Appreciation potential',
          description: 'Long-term value growth',
          isGroupTask: true,
          dataFields: [
            { key: 'historical_appreciation', label: 'Historical Appreciation', type: 'number', unit: 'percent' },
            { key: 'projected_appreciation', label: 'Projected Annual', type: 'number', unit: 'percent' },
            { key: 'appreciation_factors', label: 'Appreciation Factors', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Strong appreciation potential' },
            { flag: 'green', condition: 'Good appreciation outlook' },
            { flag: 'yellow', condition: 'Limited appreciation expected' },
          ],
        },
        {
          code: 'total_roi',
          title: 'Total ROI projection',
          description: 'Overall return analysis',
          isGroupTask: true,
          dataFields: [
            { key: 'holding_period', label: 'Holding Period', type: 'number', unit: 'years' },
            { key: 'projected_cash_flow_total', label: 'Projected Cash Flow (Total)', type: 'number', unit: 'dollars' },
            { key: 'projected_appreciation', label: 'Projected Appreciation', type: 'number', unit: 'dollars' },
            { key: 'total_return', label: 'Total Return', type: 'number', unit: 'dollars' },
            { key: 'annualized_roi', label: 'Annualized ROI', type: 'number', unit: 'percent' },
          ],
        },
      ],
    },
    {
      code: 'partner_analysis',
      name: 'Partnership Analysis',
      items: [
        {
          code: 'ownership_structure',
          title: 'Ownership structure',
          description: 'Define partnership split',
          isGroupTask: true,
          dataFields: [
            { key: 'ownership_type', label: 'Ownership Type', type: 'select', options: [
              { value: 'tenants_common', label: 'Tenants in Common' },
              { value: 'joint_tenants', label: 'Joint Tenants' },
              { value: 'llc', label: 'LLC' },
              { value: 'partnership', label: 'Partnership' },
            ]},
            { key: 'partner_shares', label: 'Partner Shares', type: 'array' },
          ],
        },
        {
          code: 'capital_contributions',
          title: 'Capital contributions',
          description: 'Each partner contribution',
          isGroupTask: true,
          dataFields: [
            { key: 'contributions', label: 'Contributions by Partner', type: 'array' },
            { key: 'total_capital', label: 'Total Capital', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'expense_sharing',
          title: 'Expense sharing agreement',
          description: 'How ongoing expenses will be split',
          isGroupTask: true,
          dataFields: [
            { key: 'expense_split_method', label: 'Split Method', type: 'string' },
            { key: 'monthly_contributions', label: 'Monthly Contributions', type: 'array' },
          ],
        },
      ],
    },
    {
      code: 'improvement_costs',
      name: 'Improvement Costs',
      items: [
        {
          code: 'immediate_repairs',
          title: 'Immediate repairs needed',
          description: 'Repairs needed at/before closing',
          isGroupTask: true,
          dataFields: [
            { key: 'repairs', label: 'Repairs', type: 'array' },
            { key: 'total_immediate', label: 'Total Immediate', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Significant immediate repairs' },
          ],
        },
        {
          code: 'planned_improvements',
          title: 'Planned improvements',
          description: 'Improvements planned post-purchase',
          isGroupTask: true,
          dataFields: [
            { key: 'improvements', label: 'Improvements', type: 'array' },
            { key: 'total_improvements', label: 'Total Improvements', type: 'number', unit: 'dollars' },
            { key: 'timeline', label: 'Timeline', type: 'string' },
          ],
        },
        {
          code: 'total_all_in_cost',
          title: 'Total all-in cost',
          description: 'Purchase + closing + improvements',
          isGroupTask: true,
          dataFields: [
            { key: 'purchase_price', label: 'Purchase Price', type: 'number', unit: 'dollars' },
            { key: 'closing_costs', label: 'Closing Costs', type: 'number', unit: 'dollars' },
            { key: 'immediate_repairs', label: 'Immediate Repairs', type: 'number', unit: 'dollars' },
            { key: 'planned_improvements', label: 'Planned Improvements', type: 'number', unit: 'dollars' },
            { key: 'total_all_in', label: 'Total All-In', type: 'number', unit: 'dollars' },
          ],
        },
      ],
    },
  ],
};

